import { CdkOverlayOrigin, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, NgModule, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { Router } from '@angular/router';
import { dateValueIsAfterOrBeforeOtherControl } from '@app/common/date-value-is-after-or-before-other-control-validator';
import { ChannelMatchingMode } from '@app/common/page-query';
import { ChannelControlModule } from '@app/components/form-controls/channel-control/channel-control.component';
import { PageType } from '@app/models/page';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { subDays } from 'date-fns';
import { combineLatest, Subscription } from 'rxjs';
import { debounceTime, filter, startWith } from 'rxjs/operators';

export enum TopNewsOnlyOption {
  All = 0,
  TopNewsOnly = 1
}

@Component({
  selector: 'app-page-filter',
  templateUrl: './page-filter.component.html',
  styleUrls: ['./page-filter.component.scss']
})
export class PageFilterComponent implements OnInit, OnDestroy {

  // Channel picker stuff
  @ViewChild('trigger') trigger: CdkOverlayOrigin;
  _domTrigger: ClientRect;
  channelMenuOpen: boolean;

  @Input() channels: Array<SimplifiedTerm>;
  @Input() editorOptionsEnabled: boolean;
  @Input() set pageType(pageType: PageType) {
    if (pageType === PageType.News) {
      this.isNews = true;
    }
  }

  @Output() queryOptionsChange = new EventEmitter<any>();
  @Output() channelOptionsChange = new EventEmitter<any>();

  languageFilteringEnabled: Boolean;
  isNews: Boolean;
  selectedChannels = new Array<SimplifiedTerm>();
  searchText: FormControl;
  queryOptionsForm: FormGroup;
  channelFilterForm: FormGroup;
  subscription: Subscription;
  filterOpen = this.featureSettingsStoreService.state.showFilterInNewsCenter;
  newsDaysBack = this.featureSettingsStoreService.state.newsNumberOfDaysBack;
  featureSettings = this.featureSettingsStoreService.state;

  constructor(private fb: FormBuilder,
              private cdRef: ChangeDetectorRef,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private router: Router) {

    const now: Date = new Date();
    this.searchText = this.fb.control('');
    this.queryOptionsForm = this.fb.group({
      onlyCurrentUserPages: [false],
      onlyInCurrentUserLanguages: [true],
      includeDrafts: false,
      startDate: [subDays(now, this.newsDaysBack ? this.newsDaysBack : 30), dateValueIsAfterOrBeforeOtherControl('endDate', false)],
      endDate: [now, dateValueIsAfterOrBeforeOtherControl('startDate', true)],
      topNewsOnlyOption: [TopNewsOnlyOption.All],
      includeArchive: false,
    });
    this.channelFilterForm = this.fb.group({
      selectedChannels: [[]],
      channelsMatchingOption: [ChannelMatchingMode.Or],
    });
  }

  ngOnInit(): void {
    // If language filtering is enabled for sharepoint or Workplace, show the 'only in my language(s)' option.
    const feature = this.featureSettingsStoreService.state;
    this.languageFilteringEnabled = feature.enableLanguageFilteringForWorkplace ||
      feature.enableLanguageFilteringForSharepointPages;

    // Combine searchtext and queryoptions changes with a debounce on searchtext
    this.subscription = combineLatest([
      this.searchText.valueChanges.pipe(
        startWith(this.searchText.value),
        filter(() => this.searchText.valid),
        debounceTime(500)
      ),
      this.queryOptionsForm.valueChanges.pipe(
        startWith(this.queryOptionsForm.value),
        filter(() => this.queryOptionsForm.valid)
      )
    ]).subscribe(([searchText, queryOptions]) => this.queryOptionsChange.next({ ...queryOptions, searchText: searchText }));

    this.subscription.add(this.channelFilterForm.valueChanges.pipe(
      filter(() => this.channelFilterForm.valid)).subscribe(
        data => {
          this.channelOptionsChange.next(data);
        }
      ));

    // apply filter from the url parameter passed from parent component page-center
    const selectedChannels = this.channels.filterRecursive(c => c.Selected, 'Terms');
    if (selectedChannels?.length > 0) {
      this.selectedChannels = selectedChannels;
      this.channelFilterForm.get('selectedChannels').setValue(selectedChannels);
    }
  }

  channelFocus() {
    this._domTrigger = this.trigger.elementRef.nativeElement.getBoundingClientRect();
    this.channelMenuOpen = !this.channelMenuOpen;
  }

  handleRemoveSelectedChannel(cn: SimplifiedTerm) {
    cn.Selected = false;
    const selectedChannelsWithoutRemovedChannel = this.selectedChannels.filter(sc => sc.Guid !== cn.Guid);
    this.handleSelectedChannelsChange(selectedChannelsWithoutRemovedChannel);
    this.cdRef.detectChanges();
  }

  handleSelectedChannelsChange(event: SimplifiedTerm[]) {
    this.selectedChannels = event;
    this.channelFilterForm.get('selectedChannels').setValue(this.selectedChannels);
    this.appendQueryParam(this.selectedChannels);
  }

  appendQueryParam(terms: Array<SimplifiedTerm>) {
    let queryParam = { channel: '' };
    if (terms.length === 1) {
      queryParam = { channel: terms[0].Guid };
    }

    const urlTree = this.router.createUrlTree([], {
      queryParams: queryParam,
      queryParamsHandling: 'merge',
      preserveFragment: true
    });

    this.router.navigateByUrl(urlTree);
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }

  toggleFilter() {
    console.log('asd');
    this.filterOpen = !this.filterOpen;
  }

}
@NgModule({
  declarations: [
    PageFilterComponent
  ],
  exports: [
    PageFilterComponent
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatExpansionModule,
    MatRadioModule,
    ChannelControlModule,
    MatIconModule,
    OverlayModule,
    MatCheckboxModule,
    MatButtonModule
  ]
})
export class PageFilterModule { }
